import { RouteSeoContent } from './route-seo-content.model';

/**
 * Per-route AIO copy for the prerendered calculator routes (GSC-intent + no hardcoded lei
 * prices). Wired into the route `data` (app-routing / calculation / approximate / rain-system
 * modules) and rendered by `RouteSeoContentComponent` so crawlers/LLMs get real body text in
 * the static HTML. The `{mp}` placeholder in `APPROXIMATE_SURFACE_AIO` is substituted with the
 * route's surface param at render time via `interpolateRouteSeoContent`.
 *
 * The card is purely a list of collapsible questions, so each block's lead intent is the FIRST
 * faq item and the supported-brands sentence is the LAST faq item — their text is the answer.
 */

/** `/calculator/ro/` — landing. */
export const LANDING_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul de tablă pentru acoperiș?',
      a: 'Calculatorul de tablă pentru acoperiș îți calculează exact cantitatea de materiale, pe baza dimensiunilor casei tale și a modelului ales, și estimează prețul. Alegi forma acoperișului (1, 2 sau 4 ape, formă de L sau 2 ape cu lucarnă) și modelul de tablă, introduci dimensiunile și numărul de hornuri, iar calculatorul generează schițele și îți afișează necesarul de table, accesoriile și, opțional, sistemul pluvial, plus costul total orientativ. Funcționează la fel și pentru garduri din șipcă metalică.',
    },
    {
      q: 'Cum calculez tabla necesară pentru acoperiș?',
      a: 'Alegi modelul de tablă, apoi introduci dimensiunile acoperișului (lățime și înălțimi) și numărul de hornuri. Calculatorul generează schițele și calculează automat numărul de foi, suprapunerile și accesoriile — nu trebuie să introduci panta. Pentru o estimare rapidă fără dimensiuni detaliate, folosește modul de calcul aproximativ după suprafață.',
    },
    {
      q: 'Cât costă tabla pentru acoperiș?',
      a: 'Costul depinde de tipul de tablă (țiglă, cutată sau fălțuită), de grosime, de finisaj și de suprafața acoperișului. Prețurile pornesc „de la” un nivel de bază (grosimi mici și finisaj standard) și cresc spre finisajele premium; calculează exact cu calculatorul, în funcție de modelul și cantitatea ta.',
    },
    {
      q: 'Calculatorul include și accesoriile?',
      a: 'Da. Pe lângă foile de tablă, calculatorul include automat accesoriile, după forma acoperișului: coamele (la 4 ape și la forma de L, și pe muchii), doliile (la forma de L și la lucarnă), bordurile de streașină, bordurile de fronton (paziile), bordura la perete (pe laturile lucarnei), șuruburile, folia anticondens și, în funcție de model, opritorii de zăpadă. Opțional adaugă și sistemul pluvial — jgheaburi și burlane — astfel încât bugetul orientativ să fie complet, nu doar pentru tablă.',
    },
    // Folded in from the landing page's former second FAQ block (2026-09-16) so the page carries ONE
    // accordion and ONE FAQPage. Fact-checked: delivery is the sitewide 2-7 business-day claim; the
    // catalogue warranty ceiling is 60 years — Novatik NATURA's certificate (impermeabilitate 60 /
    // estetic 30, live since 2026-09-08; see llms-full §garanții) — with 50 years at the top Wetterbest
    // (Suprem 50) and Caretta (Briliant Duo) finishes; it varies by model, finish and thickness. Keep in
    // sync with the trust band in featured-products.component.html.
    {
      q: 'Cât durează livrarea?',
      a: 'Livrăm oriunde în țară în 2-7 zile lucrătoare, direct de la fabrică.',
    },
    {
      q: 'Ce garanție au produsele?',
      a: 'Produsele au garanție de până la 60 de ani (Novatik NATURA; 50 de ani la finisajele de top Wetterbest și Caretta), în funcție de model, finisaj și grosime, cu certificate de garanție emise de producători — le găsești în secțiunea Informații utile a site-ului.',
    },
    {
      q: 'Ce producători de tablă pot compara?',
      a: 'Poți compara modele și prețuri pentru tablă tip țiglă, tablă cutată și tablă fălțuită de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/** `/calculator/ro/calcul-aproximativ` — hub. */
export const APPROXIMATE_HUB_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Cum funcționează calculul aproximativ de preț?',
      a: 'Costul unui acoperiș nou depinde în primul rând de suprafața lui în metri pătrați, de tipul de tablă ales (țiglă, cutată sau fălțuită), de grosime și finisaj, de numărul de ape și de accesoriile necesare. Calculul aproximativ îți dă rapid un preț orientativ pornind doar de la suprafață, fără plan detaliat — alege numărul de mp și vezi estimarea pentru materiale.',
    },
    {
      q: 'Cum estimez rapid prețul unui acoperiș?',
      a: 'Alege suprafața aproximativă a acoperișului în mp, iar calculatorul îți arată un cost orientativ pentru tablă și accesorii. Pentru un buget precis, treci apoi la calculul detaliat, unde introduci forma acoperișului și dimensiunile reale ale fiecărei ape.',
    },
    {
      q: 'De ce depinde prețul pe metru pătrat?',
      a: 'Prețul pe mp variază cu modelul de tablă, grosimea (de la 0,4 mm în sus), tipul de finisaj (lucios, mat, mate speciale sau imitație lemn) și complexitatea acoperișului. Manopera de montaj se calculează separat. Folosește calculatorul pentru intervalul corect pentru cazul tău.',
    },
    {
      q: 'Estimarea include manopera de montaj?',
      a: 'Calculul aproximativ acoperă materialele (tablă plus accesorii). Manopera de montaj pe mp se stabilește de echipa de montaj în funcție de regiune și de complexitate, așa că o adaugi separat peste costul materialelor.',
    },
    {
      q: 'Pentru ce mărci funcționează estimarea?',
      a: 'Estimarea funcționează pentru toate modelele uzuale de tablă tip țiglă și cutată de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/**
 * `/calculator/ro/calcul-aproximativ/:surface` — TEMPLATE (mp = 40…500). The `{mp}`
 * placeholder is substituted with the route's `surface` param at render time.
 */
export const APPROXIMATE_SURFACE_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'De ce depinde costul unui acoperiș de {mp} mp?',
      a: 'Costul unui acoperiș de {mp} mp depinde de tipul de tablă ales (țiglă, cutată sau fălțuită), de grosime și finisaj, de numărul de ape și de accesoriile necesare — nu doar de suprafață. Pentru {mp} mp de acoperiș, prețul materialelor pornește „de la” un nivel de bază (grosimi mici, finisaj standard) și crește spre finisajele premium; calculează valoarea exactă pentru {mp} mp cu calculatorul, alegând modelul tău.',
    },
    {
      q: 'Cât tablă îmi trebuie pentru {mp} mp de acoperiș?',
      a: 'Suprafața de tablă necesară este mai mare decât suprafața construită, pentru că include panta acoperișului și suprapunerile dintre foi. Pentru {mp} mp introdu dimensiunile reale în calculator, ca să afli numărul exact de foi și accesoriile, fără să comanzi în plus.',
    },
    {
      q: 'Prețul pentru {mp} mp include și manopera?',
      a: 'Estimarea pentru {mp} mp acoperă materialele — tablă și accesorii. Manopera de montaj se calculează separat, pe mp, în funcție de regiune și de complexitatea acoperișului, și se adaugă peste costul materialelor afișat de calculator.',
    },
    {
      q: 'Cum reduc costul pentru un acoperiș de {mp} mp?',
      a: 'Cel mai mult contează grosimea și finisajul: o grosime mai mică, potrivită expunerii (de exemplu 0,4 mm), și un finisaj standard (lucios sau mat) costă mai puțin decât finisajele premium, cum sunt cele cu aspect de lemn sau finisajele speciale. Între tablă tip țiglă și tablă cutată diferența de preț este de obicei mică, iar tabla fălțuită este mai scumpă. Compară variantele direct în calculator pentru cei {mp} mp, ca să vezi diferența de preț.',
    },
    {
      q: 'Ce tipuri de tablă pot compara pentru {mp} mp?',
      a: 'Pentru {mp} mp poți compara prețuri la tablă tip țiglă, cutată și fălțuită de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/** `/calculator/ro/calcul/1-apă/modele`. */
export const ONE_SLOPE_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul pentru acoperiș într-o apă?',
      a: 'Calculatorul pentru acoperiș într-o apă (o singură pantă) îți calculează exact tabla și accesoriile, pe baza dimensiunilor pe care le introduci, și estimează prețul. Alegi modelul de tablă, introduci lățimea și înălțimea apei și numărul de hornuri, iar aplicația generează schița și calculează numărul de foi, suprapunerile și costul orientativ al materialelor — un tip de acoperiș des folosit la case moderne, anexe și extinderi.',
    },
    {
      q: 'Ce dimensiuni îmi trebuie pentru un acoperiș într-o apă?',
      a: 'Pentru un acoperiș într-o apă introduci lățimea și înălțimea apei (versantului) și numărul de hornuri. Nu trebuie să introduci panta sau unghiul — calculatorul lucrează direct cu dimensiunile reale pe care le dai și generează schița și necesarul de tablă.',
    },
    {
      q: 'Cât costă un acoperiș într-o apă?',
      a: 'Prețul depinde de suprafață, de tipul și grosimea tablei și de accesorii. Un acoperiș într-o apă are de obicei mai puține piese de finisaj decât unul în 4 ape, deci mai puține accesorii. Calculează costul exact cu calculatorul, alegând modelul tău.',
    },
    {
      q: 'Ce tablă pot alege pentru acoperișul într-o apă?',
      a: 'Pentru acoperișul într-o apă poți alege tablă tip țiglă sau tablă cutată de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/** `/calculator/ro/calcul/2-ape/modele`. */
export const TWO_SLOPES_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul pentru acoperiș în 2 ape?',
      a: 'Calculatorul pentru acoperiș în 2 ape îți calculează exact cantitatea de tablă și accesoriile pentru cea mai răspândită formă de acoperiș din România, și estimează prețul. Alegi modelul de tablă, introduci dimensiunile celor două ape (lățime și înălțimi) și numărul de hornuri, iar aplicația generează schițele și calculează automat numărul de foi, coama, suprapunerile și costul orientativ al materialelor — fără să fie nevoie de un plan tehnic detaliat.',
    },
    {
      q: 'Cum calculez tabla pentru un acoperiș în 2 ape?',
      a: 'Introduci dimensiunile celor două ape (lățime și înălțimi) și numărul de hornuri. Aplicația generează schițele, ține cont de suprapunerile dintre foi și de coamă și afișează numărul exact de table și accesoriile necesare — fără să ceară panta.',
    },
    {
      q: 'Ce accesorii are nevoie un acoperiș în 2 ape?',
      a: 'De regulă: coama de pe mijloc, paziile laterale, bordurile, opritorii de zăpadă și sistemul pluvial. Calculatorul le adaugă automat la calcul în funcție de dimensiuni, astfel încât bugetul orientativ să acopere tot materialul, nu doar foile de tablă.',
    },
    {
      q: 'Ce modele de tablă pot compara pentru 2 ape?',
      a: 'Pentru acoperișul în 2 ape compari modele de tablă tip țiglă și cutată de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/** `/calculator/ro/calcul/4-ape/modele`. */
export const FOUR_SLOPES_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul pentru acoperiș în 4 ape?',
      a: 'Calculatorul pentru acoperiș în 4 ape îți calculează exact tabla și accesoriile pentru un acoperiș cu patru versanți, estimează prețul și generează schițe orientative ale foilor. Alegi modelul de tablă, introduci dimensiunile versanților (înălțimile și bazele triunghiurilor și ale trapezului) și numărul de hornuri, iar aplicația calculează numărul de foi și suprapunerile. Calculează și accesoriile: coama și cele patru muchii (cu aceeași piesă de coamă), bordura de streașină, șuruburile, folia anticondens, opritorii de zăpadă (în funcție de model) și, dacă îl alegi, sistemul pluvial. Un acoperiș în 4 ape are mai multe tăieturi și accesorii decât unul în 2 ape, lucru de care calculatorul ține cont automat.',
    },
    {
      q: 'Cum fac schița unui acoperiș în 4 ape?',
      a: 'Introduci dimensiunile versanților (înălțimile și bazele triunghiurilor și ale trapezului), iar calculatorul generează automat schițele orientative cu dispunerea foilor de tablă — există un pas dedicat «Schițe» în calculator. Schițele ajută la comandă și la montaj, dar pentru execuție rămâne recomandată verificarea pe teren de către echipa de montaj.',
    },
    {
      q: 'De ce costă mai mult un acoperiș în 4 ape?',
      a: 'Pentru că are patru versanți, patru muchii pe lângă coamă și mai multe tăieturi în tablă, deci mai mult deșeu și mai multe accesorii decât un acoperiș în 2 ape. Diferența reală de preț o vezi direct în calculator, comparând cele două forme.',
    },
    {
      q: 'Ce tablă pot alege pentru acoperișul în 4 ape?',
      a: 'Pentru acoperișul în 4 ape poți alege tablă tip țiglă, cutată sau fălțuită de la producători precum Bilka, Wetterbest, Caretta și Novatik.',
    },
  ],
};

/** `/calculator/ro/calcul/sipca-metalica-gard/modele`. */
export const FENCE_SLAT_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul pentru gard din șipcă metalică?',
      a: 'Calculatorul pentru gard din șipcă metalică îți calculează exact numărul de șipci și accesoriile pe baza dimensiunilor gardului, și estimează prețul. Introduci înălțimea și lățimea gardului și distanța dintre șipci, alegi modelul, iar aplicația calculează automat câte șipci metalice îți trebuie și costul orientativ al materialelor.',
    },
    {
      q: 'Cum calculez câte șipci metalice îmi trebuie pentru gard?',
      a: 'Introduci înălțimea și lățimea gardului și distanța dintre șipci, iar calculatorul calculează automat numărul de șipci necesare și accesoriile, pe baza suprafeței gardului și a distanței alese între șipci.',
    },
    {
      q: 'Cât costă un gard din șipcă metalică?',
      a: 'Prețul depinde de dimensiunile gardului (înălțime și lățime), de distanța dintre șipci și de finisajul șipcii. O distanță mai mică între șipci înseamnă mai multe șipci și un cost mai mare. Calculează valoarea exactă pentru gardul tău cu calculatorul.',
    },
    {
      q: 'Ce șipcă metalică pot alege pentru gard?',
      a: 'Șipca metalică pentru gard este disponibilă în finisaje asortate cu tabla de acoperiș de la Bilka, Wetterbest și alți producători.',
    },
  ],
};

/** `/calculator/ro/calcul/panouri-gard/modele`. */
export const FENCE_PANEL_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul pentru gard din panouri metalice?',
      a: 'Calculatorul pentru gard din panouri metalice îți calculează exact numărul de panouri și accesoriile pe baza dimensiunilor pe care le introduci, și estimează prețul. Alegi modelul de panou, introduci numărul de bucăți și dimensiunile fiecărui panou (înălțime și lățime), iar aplicația calculează automat panourile și accesoriile necesare și costul orientativ al materialelor.',
    },
    {
      q: 'Cum calculez numărul de panouri pentru gard?',
      a: 'Introduci numărul de bucăți și dimensiunile fiecărui panou (înălțime și lățime). Dacă gardul are panouri de mărimi diferite, adaugi câte o dimensiune pentru fiecare, iar calculatorul însumează panourile și accesoriile necesare.',
    },
    {
      q: 'Calculatorul funcționează și pentru garduri tip jaluzea?',
      a: 'Da. Panourile din catalog sunt sisteme de gard orizontal: lamele tip jaluzea Caretta (X121, X135, X174, plus modelele cu lamele în casetă X120, X138, X140) și panouri Bilka T18 din tablă cutată montată orizontal. Alegi modelul de jaluzele, introduci dimensiunile panourilor și primești necesarul și prețul.',
    },
    {
      q: 'Care e diferența între gard din panouri și gard din șipcă metalică?',
      a: 'Gardul din panouri folosește panouri metalice gata fabricate, montate între stâlpi, în timp ce gardul din șipcă se asamblează din șipci individuale. Pentru fiecare există un calculator separat — alege-l pe cel potrivit modelului tău.',
    },
    {
      q: 'Cât costă un gard din panouri metalice?',
      a: 'Prețul depinde de numărul și de dimensiunile panourilor, de model și de finisaj, plus accesoriile de montaj. Calculează valoarea exactă pentru gardul tău cu calculatorul.',
    },
    {
      q: 'Ce panouri de gard pot alege?',
      a: 'Poți alege panouri de gard metalice de la Bilka și Caretta — inclusiv garduri tip jaluzea cu lamele orizontale — în finisaje asortate cu tabla de acoperiș.',
    },
  ],
};

/**
 * `/calculator/ro/calcul-aproximativ-sipci-gard[/:length[/:height]]` — prerendered fence-slat
 * approximate pages (hub + length/height sizes). Generic copy (valid for every size); the
 * per-size specificity lives in the route title/h1/meta. Claims are grounded in the verified
 * `FENCE_SLAT_AIO` / `APPROXIMATE_HUB_AIO` copy above — no hardcoded prices.
 */
export const FENCE_SLATS_APPROXIMATE_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Cum funcționează calculul aproximativ pentru gard din șipcă metalică?',
      a: 'Introduci lungimea gardului în metri liniari și, opțional, înălțimea, iar calculatorul îți arată un interval de preț orientativ pentru șipcile metalice. Poți restrânge estimarea alegând producătorul, modelul, finisajul și grosimea. Pentru necesarul exact de șipci și accesorii pe dimensiunile tale, folosește apoi calculatorul detaliat de gard din șipcă metalică.',
    },
    {
      q: 'De ce depinde prețul unui gard din șipcă metalică?',
      a: 'Prețul depinde de dimensiunile gardului (lungime și înălțime), de modelul și finisajul șipcii, de grosime și de distanța dintre șipci — o distanță mai mică între șipci înseamnă mai multe șipci și un cost mai mare.',
    },
    {
      q: 'Estimarea include manopera de montaj?',
      a: 'Nu. Estimarea acoperă materialele. Manopera de montaj, stâlpii și structura gardului se calculează separat, în funcție de teren și de complexitate, și se adaugă peste costul materialelor.',
    },
    {
      q: 'Pentru ce mărci funcționează estimarea?',
      a: 'Șipca metalică pentru gard este disponibilă în finisaje asortate cu tabla de acoperiș, de la Bilka, Wetterbest și alți producători.',
    },
  ],
};

/**
 * `/calculator/ro/calcul-aproximativ-panouri-gard[/:length[/:height]]` — prerendered
 * fence-panel approximate pages (hub + length/height sizes). Same rules as the slats block.
 */
export const FENCE_PANELS_APPROXIMATE_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Cum funcționează calculul aproximativ pentru gard din panouri metalice?',
      a: 'Introduci lungimea gardului în metri liniari și, opțional, înălțimea, iar calculatorul îți arată un interval de preț orientativ pentru panourile de gard metalice. Poți restrânge estimarea alegând producătorul, modelul, finisajul și grosimea. Pentru un calcul exact pe număr de panouri și dimensiuni, folosește apoi calculatorul detaliat de gard din panouri.',
    },
    {
      q: 'De ce depinde prețul unui gard din panouri metalice?',
      a: 'Prețul depinde de numărul și de dimensiunile panourilor — rezultate din lungimea și înălțimea gardului — de model și de finisaj, plus accesoriile de montaj.',
    },
    {
      q: 'Estimarea include manopera de montaj?',
      a: 'Nu. Estimarea acoperă materialele. Manopera de montaj, stâlpii și fundația gardului se calculează separat, în funcție de teren și de complexitate, și se adaugă peste costul materialelor.',
    },
    {
      q: 'Ce panouri de gard pot alege?',
      a: 'Poți alege panouri de gard metalice de la Bilka și Caretta — inclusiv garduri tip jaluzea cu lamele orizontale — în finisaje asortate cu tabla de acoperiș.',
    },
  ],
};

/** `/calculator/ro/sistem-pluvial`. */
export const RAIN_SYSTEM_AIO: RouteSeoContent = {
  faq: [
    {
      q: 'Ce face calculatorul de sistem pluvial?',
      a: 'Calculatorul de sistem pluvial îți compune lista completă de piese pentru jgheaburi și burlane și îți calculează prețul. Alegi producătorul, varianta de sistem (diametrul sau forma jgheabului), finisajul și culoarea, iar aplicația afișează toate componentele disponibile pentru acel sistem — jgheaburi, cârlige, colțuri, îmbinări, capace, pâlnii, coturi, burlane, coliere sau brățări. Cantitățile le introduci tu, piesă cu piesă, apoi adaugi totul în coș și vezi prețul. Calculatorul nu estimează singur necesarul din dimensiunile acoperișului; pentru asta folosește calculatorul de acoperiș (1, 2 sau 4 ape), care include opțional și sistemul pluvial.',
    },
    {
      q: 'Cum dimensionez jgheaburile și burlanele?',
      a: 'Orientativ, după ghidul de montaj Bilka: pentru acoperișuri de până la 100 mp se folosește sistemul 125/90 (jgheab de 125 mm cu burlan de 90 mm), iar peste 100 mp sistemul 150/100 (alți producători au diametre apropiate, de exemplu 125/88 și 150/97 la Wetterbest). Lungimea de jgheab este egală cu lungimea streșinii, cu cel puțin o coborâre de burlan la fiecare 8 m de jgheab. Numărul de piese rezultat îl introduci în calculator; dacă preferi o estimare automată din dimensiunile casei, folosește calculatorul de acoperiș, care poate include și sistemul pluvial.',
    },
    {
      q: 'Cât costă un sistem pluvial complet?',
      a: 'Costul depinde de metrii de jgheab, de numărul de coborâri (burlane, coturi, coliere), de producător și de finisajul ales. Calculatorul adună piesele și cantitățile pe care le introduci și îți afișează prețul complet în coș, pentru exact ce ai nevoie.',
    },
    {
      q: 'Ce sisteme pluviale pot alege?',
      a: 'Sisteme pluviale metalice de la Bilka, Wetterbest, Caretta și Novatik, în finisaje și culori asortate cu tabla de acoperiș, fiecare cu propriile variante de diametru sau de formă (jgheab rotund sau rectangular).',
    },
  ],
};
